import { Notification, NotificationDisplayType } from '@this+that/models';
import { StoreApi } from 'zustand';

import { NotificationsStore } from './types';

type ZustandSet = StoreApi<NotificationsStore>['setState'];
type ZustandGet = StoreApi<NotificationsStore>['getState'];

const retirePriorUnreadBanners = (
  notifications: Notification[],
  incomingNotification: Notification,
): Notification[] => {
  const isBanner =
    incomingNotification.displayType === NotificationDisplayType.BANNER;
  const hasSourceService = Boolean(incomingNotification.sourceService);

  if (!isBanner || !hasSourceService) {
    return notifications;
  }

  return notifications.map((existing) => {
    const isSameSource =
      existing.sourceService === incomingNotification.sourceService;
    const isUnreadBanner =
      existing.displayType === NotificationDisplayType.BANNER &&
      existing.wasRead === false;

    if (isSameSource && isUnreadBanner) {
      return { ...existing, wasRead: true };
    }

    return existing;
  });
};

export const handleAddNotification = async (
  set: ZustandSet,
  get: ZustandGet,
  notification: Notification,
) => {
  const { notifications, readNotifications, hasLoadedReadTab } = get();

  if (notification.wasRead) {
    if (hasLoadedReadTab) {
      const existingIndex = readNotifications.findIndex(
        (n) => n.id === notification.id,
      );
      if (existingIndex !== -1) {
        const updated = [...readNotifications];
        updated[existingIndex] = notification;
        set({ readNotifications: updated });
      } else {
        set({ readNotifications: [...readNotifications, notification] });
      }
    }
    return;
  }

  const existingIndex = notifications.findIndex(
    (n) => n.id === notification.id,
  );

  if (existingIndex !== -1) {
    const updatedNotifications = [...notifications];
    updatedNotifications[existingIndex] = notification;
    set({ notifications: updatedNotifications });
  } else {
    const retiredNotifications = retirePriorUnreadBanners(
      notifications,
      notification,
    );
    set({ notifications: [...retiredNotifications, notification] });
  }
};

export const handleUpdateNotification = async (
  set: ZustandSet,
  get: ZustandGet,
  notification: Notification,
) => {
  const { notifications, readNotifications, hasLoadedReadTab } = get();

  const existingInUnread = notifications.find((n) => n.id === notification.id);
  const isMovingToRead =
    existingInUnread &&
    existingInUnread.wasRead === false &&
    notification.wasRead === true;

  if (isMovingToRead) {
    const filteredUnread = notifications.filter(
      (n) => n.id !== notification.id,
    );
    if (hasLoadedReadTab) {
      set({
        notifications: filteredUnread,
        readNotifications: [...readNotifications, notification],
      });
    } else {
      set({ notifications: filteredUnread });
    }
    return;
  }

  // A terminal sync message resets wasRead on a row `markAsRead` already
  // dropped from `notifications`, so the map below has nothing to update and
  // the banner would only come back on a full page load.
  //
  // Gated on the sync outcome, not on wasRead alone: `markAsRead` is optimistic
  // and a progress update that landed just before it carries the row's
  // pre-dismiss `wasRead: false`, so the looser test resurrects the banner the
  // user just closed. Only an outcome is new enough information to earn that.
  const isTerminalSyncUpdate = notification.syncTerminalAt !== undefined;

  if (
    !existingInUnread &&
    notification.wasRead === false &&
    isTerminalSyncUpdate
  ) {
    set({
      notifications: [...notifications, notification],
      readNotifications: readNotifications.filter(
        (n) => n.id !== notification.id,
      ),
    });
    return;
  }

  const existingInRead =
    hasLoadedReadTab && readNotifications.find((n) => n.id === notification.id);

  if (existingInRead) {
    // An unread row does not belong in the read list, whichever phase reopened
    // it. A run's first tick resets wasRead server-side without carrying
    // `syncTerminalAt`, so it misses the branch above and would otherwise be
    // updated in place — rendered under "Read" while the server has it unread,
    // until a full reload.
    if (notification.wasRead === false) {
      set({
        notifications: [...notifications, notification],
        readNotifications: readNotifications.filter(
          (n) => n.id !== notification.id,
        ),
      });
      return;
    }

    set({
      readNotifications: readNotifications.map((n) =>
        n.id === notification.id ? notification : n,
      ),
    });
  } else {
    set({
      notifications: notifications.map((n) =>
        n.id === notification.id ? notification : n,
      ),
    });
  }
};

export const handleRemoveNotification = async (
  set: ZustandSet,
  get: ZustandGet,
  id: string,
) => {
  const { notifications, readNotifications } = get();
  set({
    notifications: notifications.filter((n) => n.id !== id),
    readNotifications: readNotifications.filter((n) => n.id !== id),
  });
};

export const handleMarkAllAsRead = (
  set: ZustandSet,
  get: ZustandGet,
  ids: string[],
) => {
  const { notifications, readNotifications, hasLoadedReadTab } = get();
  const idSet = new Set(ids);
  const toMove = notifications.filter((n) => n.id && idSet.has(n.id));
  const remaining = notifications.filter((n) => !n.id || !idSet.has(n.id));
  const nowRead = toMove.map((n) => ({ ...n, wasRead: true }));

  if (hasLoadedReadTab) {
    set({
      notifications: remaining,
      readNotifications: [...readNotifications, ...nowRead],
    });
  } else {
    set({ notifications: remaining });
  }
};

export const handleMarkAsRead = async (
  set: ZustandSet,
  get: ZustandGet,
  id: string,
) => {
  const { notifications, readNotifications, hasLoadedReadTab } = get();

  const target = notifications.find((n) => n.id === id);
  const filteredUnread = notifications.filter((n) => n.id !== id);

  if (target) {
    const readNotification = { ...target, wasRead: true };
    if (hasLoadedReadTab) {
      set({
        notifications: filteredUnread,
        readNotifications: [...readNotifications, readNotification],
      });
    } else {
      set({ notifications: filteredUnread });
    }
  }
};

export const handleGetLatestUnreadBanner = (get: ZustandGet) => {
  const { notifications } = get();

  const unreadBanners = notifications.filter(
    (n) =>
      n.displayType === NotificationDisplayType.BANNER && n.wasRead === false,
  );

  if (unreadBanners.length === 0) {
    return null;
  }

  const sortedBanners = unreadBanners.slice().sort((a, b) => {
    const aTime = a.createdAt || 0;
    const bTime = b.createdAt || 0;
    return bTime - aTime;
  });

  return sortedBanners[0];
};

export const handleClearNotifications = async (set: ZustandSet) => {
  set({
    notifications: [],
    readNotifications: [],
    isInitialLoading: true,
    hasLoadedReadTab: false,
  });
};

export const handleSetReadNotifications = async (
  set: ZustandSet,
  notifications: Notification[],
) => {
  set({ readNotifications: notifications });
};

export const handleSetNotifications = async (
  set: ZustandSet,
  notifications: Notification[],
) => {
  set({ notifications });
};
